/**
 * useSessionGuard - 会话保护: WebSocket 实时推送 + 轮询降级。
 *
 * 工作流程:
 *   1. 有 token 时连接 WS /api/v1/ws/notify?token=xxx
 *   2. 收到 session_conflict 消息 -> showNotify 弹窗 + 跳登录
 *   3. WS 断开/失败 -> 启动 30s 轮询 GET /me 作为降级
 *   4. WS 重连成功 -> 停止轮询
 *   5. 无 token (未登录) -> 不启动, watch token 变化
 *
 * WS token 过期处理: access_token 60min 过期, 但 session_id 在 refresh 时不变,
 * 所以 WS 连接建立后即使 token 过期, kick 事件仍能通过 Redis pubsub 送达。
 * WS 断开后重连时才需要新 token, 重连时从 getAccessToken() 获取最新值。
 */
import { watch, onUnmounted, type WatchStopHandle } from 'vue'
import { http, getAccessToken, clearTokens, redirectToLogin, wsBaseUrl } from '@/api/client'
import { showNotify } from '@/stores/notify'

const WS_RECONNECT_DELAY = 3000
const POLL_INTERVAL = 30000

let ws: WebSocket | null = null
let pollTimer: ReturnType<typeof setInterval> | null = null
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let destroyed = false
let kicked = false

function startGuard(): void {
  if (destroyed) return
  connectWS()
}

function stopGuard(): void {
  destroyed = true
  if (ws) {
    ws.onclose = null
    ws.onerror = null
    ws.onmessage = null
    ws.onopen = null
    try { ws.close() } catch {}
    ws = null
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  stopPolling()
}

function connectWS(): void {
  const token = getAccessToken()
  if (!token || kicked) return

  try {
    const url = `${wsBaseUrl}/api/v1/ws/notify?token=${token}`
    ws = new WebSocket(url)
  } catch {
    startPolling()
    return
  }

  ws.onopen = () => {
    stopPolling()
  }

  ws.onmessage = (ev: MessageEvent) => {
    try {
      const data = JSON.parse(ev.data) as { type?: string; message?: string }
      if (data.type === 'session_conflict') {
        handleKick()
      }
    } catch {}
  }

  ws.onerror = () => {
    // onclose 会跟进处理
  }

  ws.onclose = () => {
    ws = null
    if (kicked || destroyed) return
    startPolling()
    scheduleReconnect()
  }
}

function scheduleReconnect(): void {
  if (reconnectTimer || kicked || destroyed) return
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    if (!kicked && !destroyed && getAccessToken()) {
      connectWS()
    }
  }, WS_RECONNECT_DELAY)
}

function startPolling(): void {
  if (pollTimer || kicked) return
  pollTimer = setInterval(pollCheck, POLL_INTERVAL)
}

function stopPolling(): void {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

async function pollCheck(): Promise<void> {
  if (kicked || !getAccessToken()) return
  try {
    await http.get('/me')
  } catch (err) {
    const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code
    if (code === 'SESSION_CONFLICT') {
      handleKick()
    }
  }
}

function handleKick(): void {
  if (kicked) return
  kicked = true
  clearTokens()
  if (ws) {
    try { ws.close() } catch {}
    ws = null
  }
  stopPolling()
  showNotify({
    title: '下线提醒',
    message: '您的账号已在其他设备登录, 您已被迫下线。如非本人操作, 请及时修改密码。',
    confirmText: '重新登录',
    variant: 'danger',
    onConfirm: () => {
      kicked = false
      redirectToLogin()
    },
  })
}

export function useSessionGuard(): void {
  let unwatch: WatchStopHandle | null = null

  unwatch = watch(
    () => getAccessToken(),
    (token, oldToken) => {
      if (token && !oldToken) {
        destroyed = false
        kicked = false
        startGuard()
      } else if (!token && oldToken) {
        stopGuard()
      }
    },
    { immediate: true },
  )

  onUnmounted(() => {
    stopGuard()
    unwatch?.()
  })
}
